'use client'

import React from 'react'
import { ArrowUp, Flag } from 'lucide-react'

/**
 * AX Climb Ladder
 *
 * Compact scan-map for the pillar's eight-step AX staircase. Each row pairs an
 * AX step with the one-line shift from its legacy root, giving readers (and
 * LLMs) the per-step "what changed" in a single pass. This complements, and
 * does not repeat, the hero staircase (labels only) and the Then-vs-Now table
 * (compares by dimension, not by step).
 *
 * Blog diagram theming: site uses data-theme dark, NOT Tailwind dark:. Shell is
 * bg-muted/50, rows bg-background, border-border/60, inline ${color} tints.
 */

const AX_BLUE = '#008dcc'
const AX_GREEN = '#3CC29E' // destination step

interface ClimbStep {
  name: string
  shift: string
}

const STEPS: ClimbStep[] = [
  {
    name: 'Traditional UX/UI research',
    shift: 'Study a relationship, not a single session',
  },
  {
    name: 'Human insights & context',
    shift: 'Design for the need for control, not just needs',
  },
  { name: 'AI literacy & prompting', shift: 'The prompt is the interface' },
  {
    name: 'Agentic experience design',
    shift: 'Capabilities, trust, and protocols',
  },
  {
    name: 'System thinking & orchestration',
    shift: 'Coordinate agents, not screens',
  },
  {
    name: 'Ethics, trust & governance',
    shift: 'Governance as a design surface',
  },
  {
    name: 'Strategic vision & future shaping',
    shift: 'Shape strategy, not layouts',
  },
  { name: 'Human + AI impact', shift: 'Outcomes neither could reach alone' },
]

export function AXClimbLadderDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <span
          className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider'
          style={{ backgroundColor: `${AX_BLUE}15`, color: AX_BLUE }}
        >
          <ArrowUp className='w-3 h-3' />
          The AX Climb
        </span>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mt-2'>
          Eight Steps, Each One a Shift
        </h3>
        <p className='text-sm text-muted-foreground mt-1'>
          The legacy foundation did not vanish. Each step leveled up for a world
          where humans and agents share control.
        </p>
      </div>

      <ol className='flex flex-col gap-2'>
        {STEPS.map((step, i) => {
          const isDestination = i === STEPS.length - 1
          const color = isDestination ? AX_GREEN : AX_BLUE
          return (
            <li
              key={step.name}
              className='flex items-center gap-3 p-3 rounded-lg bg-background border border-border/60'
              style={{ borderLeftColor: color, borderLeftWidth: 3 }}
            >
              <span
                className='flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold leading-none flex-shrink-0'
                style={{ backgroundColor: `${color}15`, color }}
              >
                {i + 1}
              </span>
              <div className='min-w-0 flex-1'>
                <span className='text-sm font-semibold text-foreground leading-tight'>
                  {step.name}
                </span>
                <p className='text-sm text-muted-foreground leading-snug mt-0.5'>
                  {step.shift}
                </p>
              </div>
              {isDestination && (
                <span
                  className='inline-flex items-center gap-1 text-xs font-semibold leading-none flex-shrink-0'
                  style={{ color: AX_GREEN }}
                >
                  <Flag className='w-3.5 h-3.5' />
                  Destination
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}
